import {
  PlaidLinkError,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOptions,
  usePlaidLink,
} from "react-plaid-link";
import { useNavigate, useLocation } from "@entry/navigation";

import { AnalyticsEvent, Flow, Routes } from "@ctypes";
import { useErrorBanner } from "@hooks/application";
import { useProcessBankLinkToken } from "@hooks/auth";
import { useOnboarding } from "@hooks/onboarding";
import { useUser } from "@hooks/user";
import { BankLinkingRouteProps } from "@routes/BankLinkingRoute/types";
import { Analytics, FintelConnect } from "@services";

export const useBankLinkingFlow = ({
  token,
  onPlaidClose,
}: {
  token: string;
  /** Called when the Plaid Link overlay is dismissed without completing. */
  onPlaidClose?: () => void;
}) => {
  const location = useLocation();

  const flow = (location.state as BankLinkingRouteProps)?.flow ?? Flow.Default;

  const [processPublicToken, { loading: isSendingToken }] = useProcessBankLinkToken();
  const { userId } = useUser();

  const { showErrorBanner } = useErrorBanner();

  const navigate = useNavigate();
  const { resetToNext } = useOnboarding();

  const handleNext = () => {
    // UserRegistrationComplete used to fire here. Bank linking is mid-onboarding
    // (see ONBOARDING_PATHS), handleNext also runs from the error branch below,
    // and the B2B flow relinks accounts for users who registered long ago - so
    // this was never the registration. It now fires once on OnboardingComplete.
    Analytics.track(AnalyticsEvent.BankLinkingSuccess);

    if (userId) {
      FintelConnect.pxl(userId);
    }

    if (flow === Flow.B2B) {
      navigate(-1);
    } else {
      resetToNext();
    }
  };

  const handleSuccess = async (
    publicToken: string | null,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    const institutionId = metadata.institution?.institution_id;

    if (!institutionId || !publicToken) return showErrorBanner();

    try {
      await processPublicToken({
        variables: {
          institutionId,
          publicToken,
        },
      });

      handleNext();
    } catch (e: any) {
      const code = e.graphQLErrors?.[0]?.extensions?.code;
      const message = e.graphQLErrors?.[0]?.message;

      const shouldRouteToUnsupportedBank = [
        Routes.BankLinking,
        Routes.InsufficientBankBalance,
      ].includes(location.pathname as Routes);

      const shouldRouteToInsufficientBankBalance = [
        Routes.BankLinking,
        Routes.UnsupportedBank,
      ].includes(location.pathname as Routes);

      if (code === "PPT0003") {
        if (shouldRouteToUnsupportedBank) {
          navigate(Routes.UnsupportedBank, { replace: true, state: { flow } });
        } else {
          showErrorBanner(message);
        }
      } else if (code === "PPT0004") {
        if (shouldRouteToInsufficientBankBalance) {
          navigate(Routes.InsufficientBankBalance, { replace: true, state: { flow } });
        } else {
          showErrorBanner(message);
        }
      } else {
        handleNext();
      }
    }
  };

  const handleExit = (err: PlaidLinkError | null) => {
    onPlaidClose?.();

    if (err?.error_code) {
      Analytics.track(AnalyticsEvent.BankLinkingExit, {
        errorCode: err.error_code,
      });

      showErrorBanner();
    }
  };

  const config: PlaidLinkOptions = {
    onSuccess: handleSuccess,
    onExit: handleExit,
    token,
  };

  const { open, ready } = usePlaidLink(config);

  return { open, ready, isSendingToken };
};
